import React, { useDeferredValue, useEffect, useMemo, useState, } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Award, BriefcaseBusiness, Building2, Info, Filter, Hash, Mail, Pencil, Phone, RotateCcw, Search, Trash2, UserRound, X, } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import SecretCodeModal from "../components/SecretCodeModal";
const API_URL = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";
const EMPTY_EDIT_FORM = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    jobTitle: "",
    department: "",
    externalId: "",
};
const getParticipantName = (participant) => {
    const modernName = [participant?.firstName, participant?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
    return participant?.fullName || modernName || participant?.name ||
        (document.documentElement.lang === "fr" ? "Sans nom" : "No name");
};
const splitLegacyName = (participant) => {
    if (participant?.firstName || participant?.lastName) {
        return {
            firstName: participant.firstName || "",
            lastName: participant.lastName || "",
        };
    }
    const parts = String(participant?.name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    return {
        firstName: parts.shift() || "",
        lastName: parts.join(" "),
    };
};
const getClientName = (participant) => {
    if (typeof participant?.client === "string")
        return participant.client;
    return participant?.client?.name ||
        (document.documentElement.lang === "fr" ? "Non renseigné" : "Not provided");
};
const getCertificateCount = (participant) => {
    const count = Number(participant?.certifsCount);
    if (Number.isFinite(count))
        return count;
    if (Array.isArray(participant?.certifs))
        return participant.certifs.length;
    return 0;
};
const normalizeObject = (value) => {
    if (!value)
        return {};
    if (value instanceof Map)
        return Object.fromEntries(value);
    if (typeof value === "object" && !Array.isArray(value))
        return value;
    return {};
};
const displayValue = (value) => {
    if (value === undefined || value === null || value === "")
        return "—";
    if (typeof value === "boolean")
        return value
            ? document.documentElement.lang === "fr" ? "Oui" : "Yes"
            : document.documentElement.lang === "fr" ? "Non" : "No";
    if (typeof value === "object") {
        try {
            return JSON.stringify(value, null, 2);
        }
        catch {
            return String(value);
        }
    }
    return String(value);
};
const formatDate = (value) => {
    if (!value)
        return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return "—";
    return new Intl.DateTimeFormat((document.documentElement.lang === "fr" ? "fr-FR" : "en-GB"), {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(date);
};
const DetailLine = ({ icon: Icon, label, value, mono = false }) => (<div className="min-w-0 rounded-xl border border-gray-200 bg-gray-50 p-3">
    <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {Icon ? <Icon className="h-3.5 w-3.5 text-blue-600"/> : null}
      {label}
    </div>
    <div className={`break-words text-sm text-gray-800 ${mono ? "font-mono text-xs" : ""}`}>
      {displayValue(value)}
    </div>
  </div>);
const ObjectSection = ({ title, value }) => {
    const entries = Object.entries(normalizeObject(value));
    if (entries.length === 0)
        return null;
    return (<section>
      <h3 className="mb-2 text-sm font-bold text-gray-900">{title}</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {entries.map(([key, entryValue]) => (<div key={key} className="rounded-lg border border-gray-200 p-3">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {key}
            </div>
            {typeof entryValue === "object" && entryValue !== null ? (<pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words text-xs text-gray-700">
                {displayValue(entryValue)}
              </pre>) : (<div className="break-words text-sm text-gray-800">
                {displayValue(entryValue)}
              </div>)}
          </div>))}
      </div>
    </section>);
};
const IconButton = ({ title, onClick, children, className = "" }) => (<button type="button" title={title} aria-label={title} onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
    }} className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${className}`}>
    {children}
  </button>);
const Participants = () => {
    const [participants, setParticipants] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [userType, setUserType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const [showFilters, setShowFilters] = useState(false);
    const [clientFilter, setClientFilter] = useState("all");
    const [organizationFilter, setOrganizationFilter] = useState("all");
    const [departmentFilter, setDepartmentFilter] = useState("all");
    const [certificateFilter, setCertificateFilter] = useState("all");
    const [sortBy, setSortBy] = useState("newest");
    const [detailsParticipant, setDetailsParticipant] = useState(null);
    const [editingParticipant, setEditingParticipant] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
    const [savingEdit, setSavingEdit] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletionCode, setDeletionCode] = useState("");
    const [deletingParticipant, setDeletingParticipant] = useState(false);
    const tr = (key, fallback) => t?.ui?.participants?.[key] || t?.[key] || fallback;
    useEffect(() => {
        const token = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");
        if (!token || !storedUser) {
            navigate("/login", { replace: true });
            return;
        }
        try {
            const parsedUser = JSON.parse(storedUser);
            setUserType(parsedUser.type);
        }
        catch {
            navigate("/login", { replace: true });
        }
    }, [navigate]);
    useEffect(() => {
        if (!userType)
            return;
        const fetchParticipants = async () => {
            setLoading(true);
            setError("");
            try {
                const token = localStorage.getItem("token");
                const requestedClientId = searchParams.get("client_id");
                const query = userType === "Admin" && requestedClientId
                    ? `?client_id=${encodeURIComponent(requestedClientId)}`
                    : "";
                const response = await fetch(`${API_URL}/api/participants${query}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message || "Impossible de charger les participants");
                }
                setParticipants(Array.isArray(data) ? data : data.participants || []);
                setSelectedClient(data.client || null);
            }
            catch (fetchError) {
                console.error("Participants fetch error:", fetchError);
                setError(fetchError.message || "Erreur lors du chargement");
            }
            finally {
                setLoading(false);
            }
        };
        fetchParticipants();
    }, [searchParams, userType]);
    const filterOptions = useMemo(() => {
        const clients = new Set();
        const organizations = new Set();
        const departments = new Set();
        participants.forEach((participant) => {
            const clientName = getClientName(participant);
            if (clientName !== tr("notProvided", "Non renseigné"))
                clients.add(clientName);
            if (participant.organization)
                organizations.add(participant.organization);
            if (participant.department)
                departments.add(participant.department);
        });
        const sortValues = (values) => [...values].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
        return {
            clients: sortValues(clients),
            organizations: sortValues(organizations),
            departments: sortValues(departments),
        };
    }, [participants]);
    const filteredParticipants = useMemo(() => {
        const query = deferredSearchTerm.trim().toLocaleLowerCase("fr");
        const result = participants.filter((participant) => {
            const searchableText = [
                getParticipantName(participant),
                participant.email,
                participant.phone,
                participant.organization,
                participant.jobTitle,
                participant.department,
                participant.externalId,
                getClientName(participant),
            ]
                .filter(Boolean)
                .join(" ")
                .toLocaleLowerCase("fr");
            const certificateCount = getCertificateCount(participant);
            return ((!query || searchableText.includes(query)) &&
                (clientFilter === "all" || getClientName(participant) === clientFilter) &&
                (organizationFilter === "all" ||
                    participant.organization === organizationFilter) &&
                (departmentFilter === "all" || participant.department === departmentFilter) &&
                (certificateFilter === "all" ||
                    (certificateFilter === "with" && certificateCount > 0) ||
                    (certificateFilter === "without" && certificateCount === 0)));
        });
        return [...result].sort((a, b) => {
            if (sortBy === "name-asc") {
                return getParticipantName(a).localeCompare(getParticipantName(b), "fr");
            }
            if (sortBy === "name-desc") {
                return getParticipantName(b).localeCompare(getParticipantName(a), "fr");
            }
            if (sortBy === "oldest") {
                return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
            }
            if (sortBy === "certificates") {
                return getCertificateCount(b) - getCertificateCount(a);
            }
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });
    }, [
        participants,
        deferredSearchTerm,
        clientFilter,
        organizationFilter,
        departmentFilter,
        certificateFilter,
        sortBy,
    ]);
    const activeFilterCount = [
        clientFilter !== "all",
        organizationFilter !== "all",
        departmentFilter !== "all",
        certificateFilter !== "all",
    ].filter(Boolean).length;
    const resetFilters = () => {
        setSearchTerm("");
        setClientFilter("all");
        setOrganizationFilter("all");
        setDepartmentFilter("all");
        setCertificateFilter("all");
        setSortBy("newest");
    };
    const openEditModal = (participant) => {
        const names = splitLegacyName(participant);
        setEditingParticipant(participant);
        setEditForm({
            ...names,
            email: participant.email || "",
            phone: participant.phone || "",
            organization: participant.organization || "",
            jobTitle: participant.jobTitle || "",
            department: participant.department || "",
            externalId: participant.externalId || "",
        });
    };
    const saveEdit = async () => {
        if (!editingParticipant)
            return;
        if (!editForm.firstName.trim() || !editForm.lastName.trim() || !editForm.email.trim()) {
            alert(tr("requiredError", "Le pr\u00E9nom, le nom et l\u2019email sont obligatoires."));
            return;
        }
        setSavingEdit(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/api/participants/${editingParticipant._id}`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(editForm),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok)
                throw new Error(data.message || "Modification impossible");
            const updatedParticipant = data.participant || {
                ...editingParticipant,
                ...editForm,
                name: `${editForm.firstName} ${editForm.lastName}`.trim(),
            };
            setParticipants((current) => current.map((participant) => participant._id === editingParticipant._id
                ? {
                    ...participant,
                    ...updatedParticipant,
                    client: participant.client,
                    certifsCount: participant.certifsCount,
                }
                : participant));
            setEditingParticipant(null);
        }
        catch (editError) {
            alert(`❌ ${editError.message}`);
        }
        finally {
            setSavingEdit(false);
        }
    };
    const deleteParticipant = (participant) => {
        setDeleteTarget(participant);
        setDeletionCode("");
    };
    const confirmDeleteParticipant = async () => {
        if (!deleteTarget || !deletionCode.trim())
            return;
        setDeletingParticipant(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/api/participants/${deleteTarget._id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ secretCode: deletionCode }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok)
                throw new Error(data.message || tr("deleteError", "Suppression impossible"));
            setParticipants((current) => current.filter((item) => item._id !== deleteTarget._id));
            if (detailsParticipant?._id === deleteTarget._id) {
                setDetailsParticipant(null);
            }
            alert(tr("deleteSuccess", "Participant supprimé définitivement. {count} certificat(s) supprimé(s).").replace("{count}", Number(data.deletedCertificates) || 0));
            setDeleteTarget(null);
            setDeletionCode("");
        }
        catch (deleteError) {
            alert(`❌ ${deleteError.message}`);
        }
        finally {
            setDeletingParticipant(false);
        }
    };
    if (loading) {
        return (<div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600"/>
      </div>);
    }
    return (<div className="min-h-screen bg-slate-50 px-3 pb-8 pt-20 sm:px-5">
      <div className="mx-auto max-w-7xl">
        <div className="relative mb-5 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-5 py-6 text-white shadow-xl shadow-slate-200/70 sm:px-7">
          <div className="absolute -right-12 -top-16 h-44 w-44 rounded-full bg-blue-500/20 blur-2xl"/>
          <div className="absolute -bottom-20 left-1/3 h-40 w-40 rounded-full bg-cyan-400/10 blur-2xl"/>
          <div className="relative flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                  <UserRound className="h-6 w-6 text-cyan-300"/>
                </span>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                    {tr("participants", "Participants")}
                  </h1>
                  <p className="mt-1 text-sm text-slate-300">{tr("subtitle", "Cliquez sur une ligne pour consulter ses certificats.")}</p>
                </div>
              </div>
            </div>
            <div className="hidden rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-right backdrop-blur sm:block">
              <div className="text-2xl font-bold text-white">{filteredParticipants.length}</div>
              <div className="text-xs font-medium uppercase tracking-wider text-slate-300">
                {selectedClient?.name || `${participants.length} au total`}
              </div>
            </div>
          </div>
        </div>

        {error ? (<div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 shadow-sm">
            {error}
          </div>) : null}

        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm shadow-slate-200/60">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"/>
              <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder={tr("searchPlaceholder", "Nom, email, organisation, fonction\u2026")} className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50/70 pl-9 pr-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100/70"/>
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setShowFilters((current) => !current)} className={`inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm font-semibold transition ${showFilters || activeFilterCount > 0
            ? "border-blue-200 bg-blue-50 text-blue-700 shadow-sm"
            : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"}`}>
                <Filter className="h-4 w-4"/>{tr("filters", "Filtres")}{activeFilterCount > 0 ? (<span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-xs text-white">
                    {activeFilterCount}
                  </span>) : null}
              </button>

              <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100/60" aria-label={tr("sortLabel", "Trier les participants")}>
                <option value="newest">{tr("sortNewest", "Plus r\u00E9cents")}</option>
                <option value="oldest">{tr("sortOldest", "Plus anciens")}</option>
                <option value="name-asc">{tr("sortNameAsc", "Nom A \u2192 Z")}</option>
                <option value="name-desc">{tr("sortNameDesc", "Nom Z \u2192 A")}</option>
                <option value="certificates">{tr("sortCertificates", "Plus de certificats")}</option>
              </select>

              {(activeFilterCount > 0 || searchTerm) && (<button type="button" onClick={resetFilters} title={tr("reset", "R\u00E9initialiser")} className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                  <RotateCcw className="h-4 w-4"/>
                </button>)}
            </div>
          </div>

          {showFilters ? (<div className="mt-3 grid grid-cols-1 gap-2 border-t border-slate-100 pt-3 sm:grid-cols-2 lg:grid-cols-4">
              {userType === "Admin" && filterOptions.clients.length > 1 ? (<select value={clientFilter} onChange={(event) => setClientFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100/60">
                  <option value="all">{tr("allClients", "Tous les clients")}</option>
                  {filterOptions.clients.map((item) => (<option key={item} value={item}>
                      {item}
                    </option>))}
                </select>) : (<div className="hidden lg:block"/>)}

              <select value={organizationFilter} onChange={(event) => setOrganizationFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100/60">
                <option value="all">{tr("allOrganizations", "Toutes les organisations")}</option>
                {filterOptions.organizations.map((item) => (<option key={item} value={item}>
                    {item}
                  </option>))}
              </select>

              <select value={departmentFilter} onChange={(event) => setDepartmentFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100/60">
                <option value="all">{tr("allDepartments", "Tous les d\u00E9partements")}</option>
                {filterOptions.departments.map((item) => (<option key={item} value={item}>
                    {item}
                  </option>))}
              </select>

              <select value={certificateFilter} onChange={(event) => setCertificateFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100/60">
                <option value="all">{tr("allCertificates", "Tous les certificats")}</option>
                <option value="with">{tr("withCertificate", "Avec certificat")}</option>
                <option value="without">{tr("withoutCertificate", "Sans certificat")}</option>
              </select>
            </div>) : null}
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
          <div className="hidden max-h-[68vh] overflow-auto md:block">
            <table className="w-full table-fixed text-left text-sm">
              <colgroup>
                <col className="w-[24%]"/>
                <col className="w-[23%]"/>
                <col className="w-[24%]"/>
                <col className="w-[12%]"/>
                <col className="w-[17%]"/>
              </colgroup>
              <thead className="sticky top-0 z-10 bg-slate-900 text-xs uppercase tracking-wider text-slate-300 shadow-sm">
                <tr>
                  <th className="px-4 py-3 font-semibold">{tr("participant", "Participant")}</th>
                  <th className="px-4 py-3 font-semibold">{tr("contact", "Contact")}</th>
                  <th className="px-4 py-3 font-semibold">{tr("professional", "Professionnel")}</th>
                  <th className="px-4 py-3 text-center font-semibold">{tr("certificates", "Certificats")}</th>
                  <th className="px-4 py-3 text-center font-semibold">{tr("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredParticipants.map((participant) => {
            const participantName = getParticipantName(participant);
            const initials = participantName
                .split(/\s+/)
                .slice(0, 2)
                .map((part) => part.charAt(0))
                .join("")
                .toUpperCase();
            const certificateCount = getCertificateCount(participant);
            return (<tr key={participant._id} role="link" tabIndex={0} onClick={() => navigate(`/certificates/${participant._id}`)} onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(`/certificates/${participant._id}`);
                    }
                }} className="cursor-pointer transition hover:bg-blue-50/80 focus:bg-blue-50/80 focus:outline-none">
                      <td className="px-4 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-xs font-bold text-white shadow-sm">
                            {initials || "?"}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate font-semibold text-gray-900" title={participantName}>
                              {participantName}
                            </div>
                            <div className="truncate text-xs text-gray-500" title={getClientName(participant)}>
                              {userType === "Admin" ? getClientName(participant) : participant.externalId || "Participant"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex min-w-0 items-center gap-2 text-gray-700">
                          <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400"/>
                          <span className="truncate" title={participant.email || ""}>
                            {participant.email || "—"}
                          </span>
                        </div>
                        {participant.phone ? (<div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-gray-500">
                            <Phone className="h-3.5 w-3.5 shrink-0"/>
                            <span className="truncate">{participant.phone}</span>
                          </div>) : null}
                      </td>

                      <td className="px-4 py-3">
                        <div className="truncate font-medium text-gray-800" title={participant.organization || ""}>
                          {participant.organization || "—"}
                        </div>
                        <div className="mt-1 truncate text-xs text-gray-500">
                          {[participant.jobTitle, participant.department]
                    .filter(Boolean)
                    .join(" · ") || tr("notProvided", "Non renseigné")}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                          <Award className="h-3.5 w-3.5"/>
                          {certificateCount}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-1.5">
                          <IconButton title={tr("viewDetails", "Voir les d\u00E9tails")} onClick={() => setDetailsParticipant(participant)} className="border-slate-200 text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                            <Info className="h-4 w-4"/>
                          </IconButton>
                          <IconButton title={tr("createCertificate", "Cr\u00E9er un certificat")} onClick={() => navigate(`/certificates/create/${participant._id}`)} className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100">
                            <Award className="h-4 w-4"/>
                          </IconButton>
                          <IconButton title={tr("edit", "Modifier")} onClick={() => openEditModal(participant)} className="border-teal-200 text-teal-700 hover:bg-teal-50">
                            <Pencil className="h-4 w-4"/>
                          </IconButton>
                          <IconButton title={tr("permanentDelete", "Supprimer d\u00E9finitivement")} onClick={() => deleteParticipant(participant)} className="border-rose-200 text-rose-600 hover:bg-rose-50">
                            <Trash2 className="h-4 w-4"/>
                          </IconButton>
                        </div>
                      </td>
                    </tr>);
        })}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-100 md:hidden">
            {filteredParticipants.map((participant) => (<div key={participant._id} role="link" tabIndex={0} onClick={() => navigate(`/certificates/${participant._id}`)} onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/certificates/${participant._id}`);
                }
            }} className="cursor-pointer p-4 transition hover:bg-blue-50 focus:bg-blue-50 focus:outline-none">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-bold text-gray-900">
                      {getParticipantName(participant)}
                    </div>
                    <div className="mt-1 truncate text-sm text-gray-600">
                      {participant.email || "—"}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {participant.organization || tr("organizationMissing", "Organisation non renseignée")}
                    </div>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                    <Award className="h-3.5 w-3.5"/>
                    {getCertificateCount(participant)}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={(event) => {
                event.stopPropagation();
                setDetailsParticipant(participant);
            }} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">{tr("details", "D\u00E9tails")}</button>
                  <button type="button" onClick={(event) => {
                event.stopPropagation();
                navigate(`/certificates/${participant._id}`);
            }} className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">{tr("viewCertificates", "Voir certificats")}</button>
                  <button type="button" onClick={(event) => {
                event.stopPropagation();
                navigate(`/certificates/create/${participant._id}`);
            }} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700">{tr("newCertificate", "Nouveau certificat")}</button>
                  <button type="button" onClick={(event) => {
                event.stopPropagation();
                deleteParticipant(participant);
            }} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100">{tr("delete", "Supprimer")}</button>
                </div>
              </div>))}
          </div>

          {filteredParticipants.length === 0 ? (<div className="px-6 py-14 text-center">
              <Search className="mx-auto mb-3 h-9 w-9 text-gray-300"/>
              <p className="font-semibold text-gray-700">{tr("noParticipant", "Aucun participant trouv\u00E9")}</p>
              <p className="mt-1 text-sm text-gray-500">{tr("noParticipantHint", "Essaie une autre recherche ou r\u00E9initialise les filtres.")}</p>
            </div>) : null}

          {filteredParticipants.length > 0 ? (<div className="border-t border-slate-100 bg-slate-50/80 px-4 py-3 text-sm text-slate-500">
              {filteredParticipants.length}{tr("displayed", "participant(s) affich\u00E9(s) \u00B7 D\u00E9filement continu")}</div>) : null}
        </div>
      </div>

      {detailsParticipant ? (<div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={(event) => {
                if (event.target === event.currentTarget)
                    setDetailsParticipant(null);
            }}>
          <aside className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
              <div className="min-w-0">
                <div className="truncate text-lg font-bold text-gray-900">
                  {getParticipantName(detailsParticipant)}
                </div>
                <div className="truncate text-sm text-gray-500">
                  {detailsParticipant.email || "—"}
                </div>
              </div>
              <button type="button" onClick={() => setDetailsParticipant(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                <X className="h-5 w-5"/>
              </button>
            </div>

            <div className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <DetailLine icon={UserRound} label={tr("fullName", "Nom complet")} value={getParticipantName(detailsParticipant)}/>
                <DetailLine icon={Hash} label={tr("externalId", "Identifiant externe")} value={detailsParticipant.externalId} mono/>
                <DetailLine icon={Mail} label={tr("email", "Email")} value={detailsParticipant.email}/>
                <DetailLine icon={Phone} label={tr("phone", "T\u00E9l\u00E9phone")} value={detailsParticipant.phone}/>
                <DetailLine icon={Building2} label={tr("organization", "Organisation")} value={detailsParticipant.organization}/>
                <DetailLine icon={BriefcaseBusiness} label={tr("jobTitle", "Fonction")} value={detailsParticipant.jobTitle}/>
                <DetailLine label={tr("department", "D\u00E9partement")} value={detailsParticipant.department}/>
                <DetailLine label={tr("client", "Client")} value={getClientName(detailsParticipant)}/>
                <DetailLine icon={Award} label={tr("certificates", "Certificats")} value={getCertificateCount(detailsParticipant)}/>
                <DetailLine label={tr("createdAt", "Cr\u00E9\u00E9 le")} value={formatDate(detailsParticipant.createdAt)}/>
                <DetailLine label={tr("updatedAt", "Modifi\u00E9 le")} value={formatDate(detailsParticipant.updatedAt)}/>
                <DetailLine label={tr("mongoId", "ID MongoDB")} value={detailsParticipant._id} mono/>
              </div>

              <ObjectSection title={tr("customFields", "Champs personnalis\u00E9s")} value={detailsParticipant.customFields}/>
              <ObjectSection title={tr("metadata", "M\u00E9tadonn\u00E9es")} value={detailsParticipant.metadata}/>

              <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                <button type="button" onClick={() => navigate(`/certificates/${detailsParticipant._id}`)} className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100">
                  {tr("viewCertificatesFull", "Voir les certificats")}
                </button>
                <button type="button" onClick={() => navigate(`/certificates/create/${detailsParticipant._id}`)} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white">{tr("createCertificate", "Cr\u00E9er un certificat")}</button>
              </div>
            </div>
          </aside>
        </div>) : null}

      <SecretCodeModal
        open={Boolean(deleteTarget)}
        title={tr("deleteParticipantTitle", "Supprimer ce participant")}
        description={tr("deleteConfirm", "Supprimer définitivement {name}{warning} ?\n\nCette action est irréversible.")
            .replace("{name}", getParticipantName(deleteTarget))
            .replace("{warning}", getCertificateCount(deleteTarget) > 0
                ? tr("deleteWarning", " ainsi que ses {count} certificat(s)").replace("{count}", getCertificateCount(deleteTarget))
                : "")}
        code={deletionCode}
        setCode={setDeletionCode}
        onClose={() => {
            if (!deletingParticipant) {
                setDeleteTarget(null);
                setDeletionCode("");
            }
        }}
        onConfirm={confirmDeleteParticipant}
        loading={deletingParticipant}
        labels={{
            protectedDeletion: tr("protectedDeletion", "Suppression protégée"),
            codeLabel: tr("deletionCodeLabel", "Code de suppression de l’administrateur"),
            codePlaceholder: tr("deletionCodePlaceholder", "Saisissez le code de suppression"),
            cancel: tr("cancel", "Annuler"),
            confirm: tr("confirmDelete", "Confirmer la suppression"),
            deleting: tr("deleting", "Suppression…"),
            close: tr("close", "Fermer"),
        }}
      />

      {editingParticipant ? (<div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm" onClick={(event) => {
                if (event.target === event.currentTarget)
                    setEditingParticipant(null);
            }}>
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <h2 className="font-bold text-gray-900">{tr("editParticipant", "Modifier le participant")}</h2>
              <button type="button" onClick={() => setEditingParticipant(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                <X className="h-5 w-5"/>
              </button>
            </div>

            <div className="max-h-[75vh] overflow-y-auto p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                ["firstName", tr("firstName", "Pr\u00E9nom"), "text", true],
                ["lastName", tr("lastName", "Nom"), "text", true],
                ["email", tr("email", "Email"), "email", true],
                ["phone", tr("phone", "T\u00E9l\u00E9phone"), "tel", false],
                ["organization", tr("organization", "Organisation"), "text", false],
                ["jobTitle", tr("jobTitle", "Fonction"), "text", false],
                ["department", tr("department", "D\u00E9partement"), "text", false],
                ["externalId", tr("externalId", "Identifiant externe"), "text", false],
            ].map(([field, label, type, required]) => (<label key={field} className="text-sm font-medium text-gray-700">
                    {label} {required ? <span className="text-red-500">*</span> : null}
                    <input type={type} required={required} value={editForm[field]} onChange={(event) => setEditForm((current) => ({
                    ...current,
                    [field]: event.target.value,
                }))} className="mt-1 h-10 w-full rounded-xl border border-gray-300 px-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100/70"/>
                  </label>))}
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={() => setEditingParticipant(null)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">{tr("cancel", "Annuler")}</button>
                <button type="button" onClick={saveEdit} disabled={savingEdit} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                  {savingEdit ? tr("saving", "Enregistrement…") : tr("save", "Enregistrer")}
                </button>
              </div>
            </div>
          </div>
        </div>) : null}
    </div>);
};
export default Participants;
